import type { IField } from '@moko/utils';
import { useFieldState } from './useFieldState';
import {
  type FieldComponentProps,
  PanelField,
  TextField,
  LinkField,
  NumberPasswordField,
  StringField,
  LookupField,
  ImageField,
  DateField,
  DateTimeField,
  CoordinateField,
  NumberWithUnitField,
  AddressField,
  RadioButtonField,
  PricingItemsField,
  CheckboxField,
  AutoCompleteField,
  SignatureField,
  FileField,
  FileViewerField,
  UserSelectorField,
  TenantSelectorField,
  ContactField,
} from './fields';

interface FieldItemProps {
  field: IField;
  fields: IField[];
}

export const FieldItem = ({ field, fields }: FieldItemProps) => {
  const {
    fieldValue,
    isFieldDisabled,
    isSaved,
    validationMessage,
    metadata,
    allOptionsObj,
  } = useFieldState(field);

  const sharedProps: FieldComponentProps = {
    field,
    fieldValue,
    isFieldDisabled,
    isSaved,
    validationMessage,
  };

  switch (field.fieldType) {
    case 'Panel':
      return <PanelField field={field} fields={fields} />;

    case 'Text':
      return <TextField field={field} fieldValue={fieldValue} />;

    case 'Link':
      return <LinkField {...sharedProps} />;

    case 'Number':
    case 'Password':
      return <NumberPasswordField {...sharedProps} />;

    case 'String':
      return <StringField {...sharedProps} />;

    case 'Lookup':
      return (
        <LookupField
          {...sharedProps}
          metadata={metadata}
          allOptionsObj={allOptionsObj}
        />
      );

    case 'Image':
      return <ImageField {...sharedProps} />;

    case 'Date':
      if (
        field.options &&
        typeof field.options === 'object' &&
        !Array.isArray(field.options) &&
        (field.options as { supportTimeInDateField?: unknown })
          .supportTimeInDateField === true
      ) {
        return <DateTimeField {...sharedProps} />;
      }
      return <DateField {...sharedProps} />;

    case 'DateTime':
      return <DateTimeField {...sharedProps} />;

    case 'Coordinate':
      return <CoordinateField {...sharedProps} />;

    case 'NumberWithUnit':
      return (
        <NumberWithUnitField
          {...sharedProps}
          metadata={metadata}
          allOptionsObj={allOptionsObj}
        />
      );

    case 'Address':
      return <AddressField {...sharedProps} />;

    case 'RadioButton':
      return <RadioButtonField {...sharedProps} metadata={metadata} />;

    case 'PricingItems':
      return <PricingItemsField {...sharedProps} />;

    case 'Checkbox':
      return <CheckboxField {...sharedProps} />;

    case 'AutoComplete':
      return <AutoCompleteField {...sharedProps} />;

    case 'UserSelector':
      return <UserSelectorField {...sharedProps} />;

    case 'TenantSelector':
      return (
        <TenantSelectorField {...sharedProps} allOptionsObj={allOptionsObj} />
      );

    case 'Signature':
      return <SignatureField {...sharedProps} />;

    case 'File':
      return <FileField {...sharedProps} />;

    case 'FileViewer':
      return <FileViewerField field={field} />;

    case 'Contact':
      return <ContactField {...sharedProps} />;

    default:
      return null;
  }
};
